import {
  AfterViewInit,
  DestroyRef,
  Directive,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import type { Observable } from 'rxjs';
import {
  animationFrameScheduler,
  asyncScheduler,
  concat as observableConcat,
  defer,
  ReplaySubject,
  Subject,
} from 'rxjs';
import {
  debounceTime,
  filter,
  finalize,
  map,
  shareReplay,
  switchMapTo,
  take,
  tap,
} from 'rxjs/operators';

export type ObservedEntry = { target: HTMLElement; width: number; height: number };

const observerSubject = new Subject<ObservedEntry>();
const resizeObserver$ = observerSubject.pipe(shareReplay(1, undefined, animationFrameScheduler));

const callback: ResizeObserverCallback = (entries: ResizeObserverEntry[]) => {
  for (const entry of entries) {
    observerSubject.next({
      target: <HTMLElement>entry.target,
      width: entry.contentRect.width,
      height: entry.contentRect.height,
    });
  }
};

const observer$: Observable<ResizeObserver> = defer(
  () =>
    <Promise<ResizeObserver>>(
      (window.ResizeObserver
        ? Promise.resolve(window.ResizeObserver)
        : import('resize-observer-polyfill').then(module => module.default))
    ),
).pipe(
  map((ctor: any) => new ctor(callback)),
  shareReplay(1),
);

@Directive({
  selector: '[rtResizeObserver]',
  exportAs: 'resizeObserver',
  standalone: true,
})
export class ResizeObserverEntryDirective implements OnInit, AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);

  @Input('rtResizeObserver') public opts: {
    initial: boolean;
  } = {
    initial: false,
  };

  @Output() public resized = new EventEmitter<ObservedEntry>();

  public get entry(): ObservedEntry | undefined {
    if (this.lastValue === undefined) {
      const bbox = this.elRef.nativeElement.getBoundingClientRect();
      return {
        target: this.elRef.nativeElement,
        width: bbox.width,
        height: bbox.height,
      };
    }

    return this.lastValue;
  }
  private sizeSubject$ = new ReplaySubject<ObservedEntry>(1);
  private lastValue: any;

  private onInit$ = new Subject<void>();
  private afterViewInit$ = new Subject<void>();

  constructor(private elRef: ElementRef) {
    const streams = [];

    if (this.opts.initial) {
      const initial$: Observable<ObservedEntry> = this.afterViewInit$.pipe(
        map(() => {
          const { width, height } = this.elRef.nativeElement.getBoundingClientRect();
          return {
            target: this.elRef.nativeElement,
            width,
            height,
          };
        }),
        take(1),
      );
      streams.push(initial$);
    }

    const resize$ = resizeObserver$.pipe(
      filter((entry: ObservedEntry) => entry.target === this.elRef.nativeElement),
      debounceTime(0, asyncScheduler),
    );

    streams.push(resize$);

    this.onInit$
      .pipe(switchMapTo(observer$))
      .subscribe(observer => observer.observe(this.elRef.nativeElement));

    this.sizeSubject$
      .asObservable()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(entry => this.resized.emit(entry));

    observableConcat(...streams)
      .pipe(
        tap(value => (this.lastValue = value)),
        finalize(() =>
          observer$
            .pipe(take(1))
            .subscribe(observer => observer.unobserve(this.elRef.nativeElement)),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((entry: ObservedEntry) => this.sizeSubject$.next(entry));
  }

  public ngOnInit(): void {
    this.onInit$.next();
  }

  public ngAfterViewInit(): void {
    this.afterViewInit$.next();
  }
}
